import { useEffect } from 'react';

import {
  actionGetRetailerCharacterDetails,
  actionGetRetailerCharacterEssences,
  actionGetRetailerCharacterFoodPairing,
  actionGetRetailerCharacters,
} from '@lib/core/characters/slices';
import { actionGetEssenceGroups } from '@lib/core/essences/slices/groups';
import { actionGetRecycling } from '@lib/core/productAttributes/slices/recycling';
import { actionGetWellness } from '@lib/core/productAttributes/slices/wellness';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { actionGetRetailerDetail, actionGetRetailerLocations } from '@lib/core/retailers/slices';
import { isApplicationKiosk } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { actionGetServiceTermsOptions } from '@lib/core/serviceTerms/slices';
import { useAnonymousUser, useUser } from '@lib/core/users/hooks';
import { actionGetDietaryPreferenceOptionsData } from '@lib/core/users/slices/dietaryPreference';
import { actionGetLocationList } from '@lib/core/users/slices/locationList';
import { actionGetProductRateOptions } from '@lib/core/users/slices/productRate';
import { actionGetProfileOptionsData } from '@lib/core/users/slices/profile';
import { actionGetExploreLocations } from '@lib/tools/explore/slices';
import { actionInitGamification } from '@lib/tools/gamification/slices';
import { PRODUCT_CATEGORY_NONE } from '@lib/tools/shared/helpers/consts';
import { fetchTastePathProducts } from '@lib/tools/tastePathProducts/slices/index';
import { useAddons, useEffectSkipFirst } from '@lib/tools/views/hooks';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';

export const DataProvider = ({ children }) => {
  const dispatch = useTypedDispatch();

  const { isEnableTastePathAddon, isEventsGamingAddon, isFreeFormTastingAddon, isGamifiedSurveyQuestionsAddon } =
    useAddons();
  const isGameAddon = isEventsGamingAddon || isFreeFormTastingAddon;
  const { retailerSlug } = useRetailer();
  const { retailerLocationId, retailerLocationProducerId } = useRetailerLocation();
  const {
    isUserAuthenticated,
    isUserHasCharacterForCurrentPC,
    userBestMatchCharacterForCurrentServiceProductCategory,
    userBestMatchCharacterByProductCategoryIdentifiers,
  } = useUser();
  const { locale, productCategory } = useApp();
  const { isLoadingAnonymousData } = useAnonymousUser();

  const shouldInitGamification =
    isUserAuthenticated &&
    !isLoadingAnonymousData &&
    ((isGameAddon && isUserHasCharacterForCurrentPC) || isGamifiedSurveyQuestionsAddon);

  useEffectSkipFirst(() => {
    if (!retailerSlug || !retailerLocationId) return;
    dispatch(actionGetRetailerDetail({ retailerSlug }));
    dispatch(
      actionGetRetailerLocations({
        retailerLocationId,
        retailerSlug,
      }),
    );

    if (isEnableTastePathAddon && isUserHasCharacterForCurrentPC) {
      dispatch(fetchTastePathProducts());
    }

    if (shouldInitGamification) {
      dispatch(actionInitGamification());
    }
  }, [locale]);

  useEffect(() => {
    // Can be removed, but also should be just modified when we go to multi-product again
    const groupedDispatch = ({ characterId }: { characterId: string }) => {
      dispatch(actionGetRetailerCharacterEssences({ characterId }));
      dispatch(actionGetRetailerCharacterDetails({ characterId }));
      dispatch(actionGetRetailerCharacterFoodPairing({ characterId }));
    };

    if (userBestMatchCharacterForCurrentServiceProductCategory?.characterId) {
      groupedDispatch({ characterId: userBestMatchCharacterForCurrentServiceProductCategory?.characterId });
    }
  }, [
    userBestMatchCharacterByProductCategoryIdentifiers,
    userBestMatchCharacterForCurrentServiceProductCategory?.characterId,
    locale,
  ]);

  useEffect(() => {
    if (!retailerSlug || productCategory === PRODUCT_CATEGORY_NONE) return;
    dispatch(actionGetRetailerCharacters());
    dispatch(actionGetEssenceGroups());
    dispatch(actionGetWellness());
    dispatch(actionGetRecycling());
    dispatch(actionGetDietaryPreferenceOptionsData());
    dispatch(actionGetProfileOptionsData());

    if (!isApplicationKiosk) {
      dispatch(actionGetProductRateOptions());
      dispatch(actionGetServiceTermsOptions());
    }
  }, [locale, productCategory]);

  useEffect(() => {
    if (isUserAuthenticated && !isApplicationKiosk) {
      dispatch(actionGetLocationList());
      dispatch(actionGetExploreLocations({ producerId: retailerLocationProducerId, retailerSlug }));
    }
  }, [isUserAuthenticated, locale]);

  useEffect(() => {
    if (shouldInitGamification) {
      dispatch(actionInitGamification());
    }
  }, [shouldInitGamification]);

  return children;
};
